import { fetchWhatsAppDocuments } from '../lib/supabase/api/fetch-whatsapp-documents';
import { v4 as uuidv4 } from 'uuid';

// Helper function to generate unique ID
export const generateUniqueId = () => {
  return uuidv4().substr(0, 8);
};

export interface WhatsAppDownloadResult {
  success: boolean;
  downloadedCount: number;
  failedCount: number;
  files: File[];
  errors?: Error[];
}

/**
 * Downloads documents from WhatsApp sessions
 * This service integrates with the existing document upload flow
 */
export class WhatsAppDocumentService {
  /**
   * Download WhatsApp documents from a session
   * @param sessionId - The WhatsApp session ID to download files from
   * @param signal - Optional AbortSignal for cancelling requests
   * @returns Download result with files ready for processing
   */
  static async downloadWhatsAppDocuments(sessionId: string, signal?: AbortSignal): Promise<WhatsAppDownloadResult> {
    try {
      // Get WhatsApp session details and document URLs
      const session = await fetchWhatsAppDocuments(sessionId);
      
      if (!session || !session.document_urls || session.document_urls.length === 0) {
        return {
          success: false,
          downloadedCount: 0,
          failedCount: 0,
          files: [],
          errors: [new Error(`No documents found for session: ${sessionId}`)]
        };
      }

      console.log(`Found ${session.document_count} documents in WhatsApp session ${sessionId}`);
      
      // Prepare result tracking
      const result: WhatsAppDownloadResult = {
        success: false,
        downloadedCount: 0,
        failedCount: 0,
        files: [],
        errors: []
      };      
      
      // Process each document URL
      for (let documentUrl of session.document_urls) {
        try {
          // Check if operation was aborted
          if (signal?.aborted) {
            throw new Error('Operation cancelled');
          }

          // Clean up URL if needed
          if (typeof documentUrl === 'string' && documentUrl.endsWith('?')) {
            documentUrl = documentUrl.slice(0, -1);
          }
          
          console.log(`Downloading WhatsApp document from URL: ${documentUrl}`);
          
          // Download file from URL with signal
          const response = await fetch(documentUrl, { signal });
          if (!response.ok) {
            throw new Error(`Failed to download document: ${response.status} ${response.statusText}`);
          }          
          
          // Get file blob and create File object
          const blob = await response.blob();
          
          // Extract filename from URL or create a default one
          let fileName = '';
          try {
            // First try to get original filename if available
            const originalName = session.file_names?.[result.downloadedCount];
            if (originalName) {
              fileName = originalName;
            } else {
              // Parse the URL to extract the filename
              const url = new URL(documentUrl);
              // Get the last part of the pathname
              const pathSegments = url.pathname.split('/');
              fileName = pathSegments[pathSegments.length - 1];
            }
          } catch (e) {
            // Fallback if URL parsing fails
            const urlParts = documentUrl.split('/');
            fileName = urlParts[urlParts.length - 1].split('?')[0];
          }
          
          // Use a default name if extraction failed
          if (!fileName) {
            fileName = `whatsapp_doc_${generateUniqueId()}.pdf`;
          }
          
          // Determine correct MIME type based on file extension
          let mimeType = blob.type;
          const extension = fileName.split('.').pop()?.toLowerCase();
          
          if (!mimeType || mimeType === 'application/octet-stream') {
            if (extension === 'pdf') {
              mimeType = 'application/pdf';
            } else if (extension === 'jpg' || extension === 'jpeg') {
              mimeType = 'image/jpeg';
            } else if (extension === 'png') {
              mimeType = 'image/png';
            } else if (extension === 'gif') {
              mimeType = 'image/gif';
            } else if (extension === 'docx') {
              mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            }
          }
          
          // Create File object with proper headers and MIME type
          const file = new File([blob], fileName, { 
            type: mimeType,
          });
          result.files.push(file);
          result.downloadedCount++;
        } catch (error) {
          if (signal?.aborted) {
            console.log('Download cancelled');
            break;
          }
          console.error('Error downloading WhatsApp document:', error);
          result.failedCount++;
          if (error instanceof Error) {
            result.errors?.push(error);
          } else {
            result.errors?.push(new Error('Unknown error processing document'));
          }
        }
      }

      // Update success status based on download results
      result.success = result.downloadedCount > 0;
      return result;
    } catch (error) {
      console.error('Error in WhatsApp document processing service:', error);
      return {
        success: false,
        downloadedCount: 0,
        failedCount: 1,
        files: [],
        errors: [error instanceof Error ? error : new Error('Unknown error')]
      };
    }
  }
}
